/**
 * Digital Role - Convert Pillar
 *
 * Conversão entre unidades de armazenamento digital usando hub-and-spoke (via byte).
 *
 * @example
 * import { convertDigital, toBaseDigital, fromBaseDigital } from '@attrx/role-morphic/digital/convert';
 *
 * convertDigital('kilobyte', 'byte', 1);     // 1000
 * convertDigital('kibibyte', 'byte', 1);     // 1024
 * toBaseDigital('megabyte', 1);              // 1000000 (bytes)
 * fromBaseDigital('mebibyte', 1048576);      // 1
 */

import { DIGITAL_UNITS, type DigitalUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Unidade base para conversões (hub) */
export const DIGITAL_BASE: DigitalUnit = "byte";

// =============================================================================
// CORE CONVERSION
// =============================================================================

/**
 * Converte valor para a unidade base (byte)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em bytes
 *
 * @example
 * toBaseDigital('kilobyte', 1);   // 1000
 * toBaseDigital('kibibyte', 1);   // 1024
 */
export function toBaseDigital(variant: DigitalUnit, value: number): number {
  const config = DIGITAL_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown digital unit: ${variant}`);
  }
  return value * (config.factor ?? 1);
}

/**
 * Converte valor da unidade base (byte) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em bytes
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseDigital('kilobyte', 1000);   // 1
 * fromBaseDigital('kibibyte', 1024);   // 1
 */
export function fromBaseDigital(variant: DigitalUnit, baseValue: number): number {
  const config = DIGITAL_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown digital unit: ${variant}`);
  }
  return baseValue / (config.factor ?? 1);
}

/**
 * Converte entre duas unidades de armazenamento digital
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertDigital('kilobyte', 'byte', 1);     // 1000
 * convertDigital('kibibyte', 'byte', 1);     // 1024
 * convertDigital('gigabyte', 'gibibyte', 1); // ~0.931
 */
export function convertDigital(
  from: DigitalUnit,
  to: DigitalUnit,
  value: number,
): number {
  if (from === to) {
    return value;
  }
  const baseValue = toBaseDigital(from, value);
  return fromBaseDigital(to, baseValue);
}

/**
 * Versão safe de convertDigital que retorna Result
 *
 * @example
 * tryConvertDigital('kilobyte', 'byte', 1);    // { ok: true, value: 1000 }
 * tryConvertDigital('invalid', 'byte', 1);     // { ok: false, error: '...' }
 */
export function tryConvertDigital(
  from: DigitalUnit,
  to: DigitalUnit,
  value: number,
): Result<number> {
  try {
    return { ok: true, value: convertDigital(from, to, value) };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Verifica se uma variante existe
 *
 * @example
 * hasDigitalVariant('byte');     // true
 * hasDigitalVariant('invalid');  // false
 */
export function hasDigitalVariant(variant: string): variant is DigitalUnit {
  return variant in DIGITAL_UNITS;
}

/**
 * Retorna lista de todas as variantes disponíveis
 *
 * @example
 * getDigitalVariants();  // ['bit', 'byte', 'kibibyte', ...]
 */
export function getDigitalVariants(): DigitalUnit[] {
  return Object.keys(DIGITAL_UNITS) as DigitalUnit[];
}
